import { useRef, useState } from "react";
import { Camera, Upload, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 2 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

export default function AvatarUploader() {
  const { user, refresh } = useAuth();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED.includes(file.type)) {
      setError("Formato no permitido. Usa JPG, PNG o WEBP.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("Imagen demasiado grande (máx 2 MB).");
      return;
    }
    setBusy(true);
    try {
      const b64 = await fileToBase64(file);
      await api.post("/auth/avatar", { mime: file.type, data_b64: b64 });
      await refresh();
      setVersion((v) => v + 1);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!window.confirm("¿Quitar tu fotografía de perfil?")) return;
    setBusy(true);
    setError("");
    try {
      await api.delete("/auth/avatar");
      await refresh();
      setVersion((v) => v + 1);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  const hasAvatar = !!user.has_avatar;
  const initials = (user.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-5" data-testid="avatar-uploader">
      <div className="relative">
        <div className="w-24 h-24 rounded-full overflow-hidden ring-2 ring-[hsl(var(--accent))]/30 bg-white/5">
          {hasAvatar ? (
            <img
              key={version}
              src={`${BACKEND}/api/users/${user.id}/avatar?v=${version}`}
              alt={user.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center serif text-3xl text-foreground/70">
              {initials}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={onPick}
          disabled={busy}
          className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-[hsl(var(--accent))] text-background flex items-center justify-center hover:brightness-110 disabled:opacity-50 shadow-lg"
          data-testid="avatar-pick"
          title="Cambiar foto"
        >
          <Camera size={16} />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">Fotografía de perfil</div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
          {hasAvatar
            ? "Aparecerás en el carrusel público de Embajadores si eres Pionero o Fundador."
            : "Sube una foto profesional (JPG/PNG/WEBP, máx 2MB) para destacar en el carrusel de Embajadores."}
        </div>
        <div className="mt-2 flex gap-2 flex-wrap">
          <button
            type="button"
            onClick={onPick}
            disabled={busy}
            className="btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="avatar-upload-btn"
          >
            <Upload size={12} /> {busy ? "Procesando…" : hasAvatar ? "Cambiar" : "Subir foto"}
          </button>
          {hasAvatar && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="text-xs inline-flex items-center gap-1.5 text-muted-foreground hover:text-red-400 disabled:opacity-50"
              data-testid="avatar-remove-btn"
            >
              <Trash2 size={12} /> Quitar
            </button>
          )}
        </div>
        {error && <div className="mt-2 text-xs text-red-400" data-testid="avatar-error">{error}</div>}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={onFile}
        className="hidden"
        data-testid="avatar-input"
      />
    </div>
  );
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });
}
